import { useEffect, useState } from 'react';

import { LOCALES } from '../lib/i18n';
import { useKiosk, type FontScale } from '../lib/session';

const FONT_SCALES: FontScale[] = ['A', 'A+', 'A++'];

/** 時鐘更新間隔——只顯示到分，15 秒足夠且不會每秒重繪整個外框。 */
const CLOCK_TICK_MS = 15_000;

/** 時間格式依語系走各自習慣（繁中「上午 12:00」、英文「12:00 AM」、日文「午前0:00」）。 */
const TIME_LOCALE: Record<string, string> = {
  'zh-TW': 'zh-TW',
  en: 'en-US',
  ja: 'ja-JP',
};

/**
 * 頁首識別帶 — 業主 2026-08-18 設計稿。
 *
 * 由左至右：品牌標誌與名稱 · 字級 · 語言 · 時間日期。
 *
 * ## 字級鍵是刻意留下的
 *
 * 設計稿上沒有畫 A／A+／A++，但同一份文件明寫「提升無障礙體驗：考量設備高度，
 * 降低輪椅使用者、高齡族群的操作門檻」。為了對齊一張示意圖而拿掉既有的放大功能，
 * 與那句要求正好相反，所以保留，只是縮到不搶識別。
 *
 * ## 標誌用業主交付的標準組合圖
 *
 * 金色波浪標誌加中英文名是一組固定的識別，尺寸與字距都由業主定死，用網頁字型
 * 重排只會做出一個「很像但不是」的東西。故直接用交付的 PNG
 * （2026-08-19《票務系統UI素材》首頁 1_02.png，468×56 去背）。
 *
 * 它不隨語系變化——品牌名本來就只有一種寫法。alt 給中文全名，讀屏才知道這是誰。
 */
const BRAND_LOCKUP_SRC = '/brand/formosa-lockup.png';

export function BrandHeader(): JSX.Element {
  const { locale, setLocale, fontScale, setFontScale } = useKiosk();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const tag = TIME_LOCALE[locale] ?? 'zh-TW';
  const time = now.toLocaleTimeString(tag, { hour: 'numeric', minute: '2-digit' });
  const date = now.toLocaleDateString(tag, { year: 'numeric', month: 'numeric', day: 'numeric' });

  return (
    <header className="k-header">
      <div className="k-brand">
        <img className="k-brand__lockup" src={BRAND_LOCKUP_SRC} alt="福爾摩沙遊艇酒店" />
      </div>

      <div className="k-header__tools">
        <div className="k-fontscale" role="group" aria-label="字級">
          {FONT_SCALES.map((f) => (
            <button
              key={f}
              type="button"
              className={`k-pill k-pill--sm${fontScale === f ? ' k-pill--active' : ''}`}
              aria-label={`字級 ${f}`}
              aria-pressed={fontScale === f}
              onClick={() => setFontScale(f)}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="k-langs" role="group" aria-label="語言">
          {LOCALES.map((l, i) => (
            <span key={l.code} className="k-langs__item">
              {i > 0 && <span className="k-langs__sep" aria-hidden="true" />}
              <button
                type="button"
                className={`k-lang${locale === l.code ? ' k-lang--active' : ''}`}
                aria-pressed={locale === l.code}
                onClick={() => setLocale(l.code)}
              >
                {l.label}
              </button>
            </span>
          ))}
        </div>

        {/* 時間對現場旅客是實用資訊（趕不趕得上下一班），設計稿把它放在最右 */}
        <div className="k-clock">
          <span className="k-clock__time">{time}</span>
          <span className="k-clock__date">{date}</span>
        </div>
      </div>
    </header>
  );
}
